'use client'

import { twMerge } from '@/lib/classname/twMerge'
import { useTranslation } from '@/lib/i18n/useTranslation.client'
import type { WebViewIntent } from '@/lib/react-native/services/react-native.intent'
import type { NonNullRequired } from '@/lib/typescript/NonNullRequired'
import { Button } from '@/ui/buttons/button/Button.client'
import { Icon } from '@/ui/icons/icon/Icon'
import type { VariantProps } from 'class-variance-authority'
import { cva } from 'class-variance-authority'
import { Group, Input, NumberField } from 'react-aria-components'
import { QuantityPickerReadOnly } from './_components/QuantityPickerReadOnly.client'

export const DEFAULT_MAX_QUANTITY = 99999

export const quantityPickerSizes = {
  sm: 'min-w-18 h-5',
  md: 'min-w-20 h-6',
}

const quantityPickerCva = cva('', {
  variants: {
    size: quantityPickerSizes,
  },
})

type QuantityPickerVariantProps = NonNullRequired<VariantProps<typeof quantityPickerCva>>

type QuantityPickerBaseProps = UiComponentProps<{
  value: number
}>

export type QuantityPickerInteractiveProps = QuantityPickerBaseProps &
  QuantityPickerVariantProps & {
    isReadOnly?: false

    /** The step value for incrementing or decrementing the quantity */
    step?: number

    /** Is disabled */
    isDisabled?: boolean

    /** Maximum allowed quantity */
    maxQuantity?: number | null

    /** Minimum allowed quantity. Defaults to `step` for backward compatibility. */
    minQuantity?: number | null

    /** The function triggered when the quantity changes */
    onQuantityChange?: (quantity: number) => void

    intent?: WebViewIntent
  }

export type QuantityPickerReadOnlyProps = QuantityPickerBaseProps & {
  isReadOnly: true
}

export type QuantityPickerProps = QuantityPickerInteractiveProps | QuantityPickerReadOnlyProps

function QuantityPickerInteractive({
  value,
  size,
  step = 1,
  maxQuantity: givenMaxQuantity,
  minQuantity: givenMinQuantity,
  onQuantityChange,
  isDisabled = false,
  intent,
  id,
  'data-testid': dataTestId,
  'aria-label': ariaLabel,
}: QuantityPickerInteractiveProps) {
  const { t } = useTranslation(['QuantityPicker'])

  const maxQuantity = givenMaxQuantity || DEFAULT_MAX_QUANTITY
  const minQuantity = givenMinQuantity ?? step

  const canIncrement = value + step <= maxQuantity
  const canDecrement = value - step >= minQuantity

  const incrementLabel = t('QuantityPicker:increment', { step, count: step })
  const decrementLabel = t('QuantityPicker:decrement', { step, count: step })

  return (
    <NumberField
      id={id}
      aria-label={ariaLabel ?? t('QuantityPicker:label')}
      value={value}
      minValue={minQuantity}
      maxValue={maxQuantity}
      step={step}
      formatOptions={{
        minimumFractionDigits: 0,
        maximumFractionDigits: 0,
      }}
      incrementAriaLabel={incrementLabel}
      decrementAriaLabel={decrementLabel}
      data-testid={dataTestId ?? 'quantity-picker'}
      onKeyDown={(e) => {
        if (e.key === 'Enter') {
          e.currentTarget.blur()
        }
      }}
      isDisabled={isDisabled}
      isReadOnly={isDisabled}
    >
      <Group
        className={twMerge(
          'relative flex flex-row items-center gap-1 rounded-full border-px border-brand-2-200 bg-white p-0.5 text-brand-2-900 hd:border-1.5',
          isDisabled && 'border-brand-2-400 text-brand-2-400',
          quantityPickerCva({ size }),
        )}
      >
        <Input
          className={twMerge(
            'w-1 grow bg-white text-center font-bold outline-none',
            isDisabled && 'cursor-not-allowed',
          )}
          disabled={isDisabled}
          data-testid="quantity-picker/field"
          onBlur={(e) => {
            const inputValue = e.target.value.replace(/[.,'\s]/g, '')

            const parsed = parseInt(inputValue)

            if (!parsed) {
              // if user leaves an empty field or an invalid value (ex: ',45') or 0
              onQuantityChange?.(minQuantity)

              return
            }

            // clamp to maxQuantity, then round down to the nearest step multiple
            const clamped = Math.min(parsed, maxQuantity)
            const rounded = Math.max(Math.floor(clamped / step) * step, minQuantity)
            onQuantityChange?.(rounded)
          }}
          // Ignore password managers extensions
          data-1p-ignore="true" // ignore 1Password extension
          data-lpignore // ignore LastPass extension
          data-form-type="other" // ignore Dashlane extension
        />
        <Button
          slot="increment"
          variant="tertiary"
          isRound
          size={size}
          iconLeft={<Icon.Plus />}
          onPress={() => canIncrement && onQuantityChange?.(value + step)}
          isDisabled={isDisabled || !canIncrement}
          className={twMerge('order-last', isDisabled && 'cursor-not-allowed')}
          intent={intent}
        >
          {incrementLabel}
        </Button>
        <Button
          slot="decrement"
          size={size}
          variant="tertiary"
          isRound
          iconLeft={<Icon.Minus />}
          onPress={() => canDecrement && onQuantityChange?.(value - step)}
          isDisabled={isDisabled || !canDecrement}
          className={twMerge('order-first', isDisabled && 'cursor-not-allowed')}
          intent={intent}
        >
          {decrementLabel}
        </Button>
      </Group>
    </NumberField>
  )
}

export function QuantityPicker(props: QuantityPickerProps) {
  if (props.isReadOnly) {
    return <QuantityPickerReadOnly {...props} />
  }

  return <QuantityPickerInteractive {...props} />
}
